<template>
  <div class="informacion-evento">
    <div v-if="loading" class="loading-wrap">
      <v-progress-circular :size="48" color="orange" indeterminate></v-progress-circular>
      <p class="loading-text">Cargando evento...</p>
    </div>

    <template v-else>
      <!-- Hero: imagen + nombre + ciudad -->
      <header class="evento-hero">
        <div class="hero-image-wrap">
          <v-img
            :src="evento.image_full_path"
            class="hero-image"
          ></v-img>
          <div class="hero-overlay">
            <h1 class="hero-title">{{ evento.nombre }}</h1>
            <p class="hero-subtitle">
              <v-icon small dark>place</v-icon>
              {{ evento.ciudad }}
            </p>
          </div>
        </div>
        <div v-if="evento.disciplinas && evento.disciplinas.length" class="hero-tags">
          <v-chip
            v-for="tag in evento.disciplinas"
            :key="tag.id"
            small
            outlined
            color="white"
            class="hero-chip"
          >
            {{ tag.nombre }}
          </v-chip>
        </div>
      </header>

      <main class="evento-main">
        <!-- Datos clave en cards escaneables -->
        <section class="section section-datos" id="info">
          <h2 class="section-title">
            <v-icon color="orange">info</v-icon>
            Información
          </h2>

          <div class="datos-grid">
            <div class="dato-card">
              <v-icon color="orange" class="dato-icon">place</v-icon>
              <div>
                <span class="dato-label">Lugar</span>
                <span class="dato-value">{{ evento.lugar }}</span>
              </div>
            </div>
            <div class="dato-card">
              <v-icon color="orange" class="dato-icon">calendar_today</v-icon>
              <div>
                <span class="dato-label">Fecha</span>
                <span class="dato-value">{{ evento.fecha_hora | formatDateString }}</span>
              </div>
            </div>
            <div class="dato-card">
              <v-icon color="orange" class="dato-icon">schedule</v-icon>
              <div>
                <span class="dato-label">Hora</span>
                <span class="dato-value">{{ evento.fecha_hora | formatTime }}</span>
              </div>
            </div>
            <div class="dato-card dato-card--inscripcion">
              <v-icon color="green" class="dato-icon">edit</v-icon>
              <div>
                <span class="dato-label">Inscripción desde</span>
                <span class="dato-value">{{ evento.fecha_inicio_inscripcion | formatDateTimeString }}</span>
              </div>
            </div>
            <div class="dato-card dato-card--inscripcion">
              <v-icon color="red" class="dato-icon">edit</v-icon>
              <div>
                <span class="dato-label">Inscripción hasta</span>
                <span class="dato-value">{{ evento.fin_inscripcion | formatDateTimeString }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Descripción -->
        <section v-if="evento.descripcion" class="section section-descripcion">
          <h2 class="section-title">
            <v-icon color="orange">article</v-icon>
            Sobre el evento
          </h2>
          <div class="descripcion-content" v-html="evento.descripcion"></div>
        </section>

        <!-- Modalidades y categorías -->
        <section class="section section-modalidades">
          <h2 class="section-title">
            <v-icon color="orange">list</v-icon>
            Modalidades
          </h2>
          <template v-if="evento.modalidades && evento.modalidades.length">
            <div class="modalidades-cards">
              <div
                v-for="modalidad in evento.modalidades"
                :key="modalidad.id"
                class="modalidad-block"
              >
                <div class="modalidad-header">
                  <v-icon small color="orange" class="modalidad-icon">sports</v-icon>
                  <span class="modalidad-nombre">{{ modalidad.nombre }}</span>
                </div>
                <template v-if="hayCategoriasVisibles(modalidad)">
                  <div class="categorias-wrap">
                    <div
                      v-if="getCategoriasEnModalidadPorSexo(modalidad, 'FEM').length > 0"
                      class="categoria-col"
                    >
                      <div class="categoria-col-title">Femeninos</div>
                      <ul class="categoria-list">
                        <li
                          v-for="cat in getCategoriasEnModalidadPorSexo(modalidad, 'FEM')"
                          :key="cat.id"
                        >
                          {{ getCategoriaNombre(cat) }}
                        </li>
                      </ul>
                    </div>
                    <div
                      v-if="getCategoriasEnModalidadPorSexo(modalidad, 'MASC').length > 0"
                      class="categoria-col"
                    >
                      <div class="categoria-col-title">Masculinos</div>
                      <ul class="categoria-list">
                        <li
                          v-for="cat in getCategoriasEnModalidadPorSexo(modalidad, 'MASC')"
                          :key="cat.id"
                        >
                          {{ getCategoriaNombre(cat) }}
                        </li>
                      </ul>
                    </div>
                    <div
                      v-if="getCategoriasEnModalidadPorSexo(modalidad, 'MIX').length > 0"
                      class="categoria-col"
                    >
                      <div class="categoria-col-title">Mixtos</div>
                      <ul class="categoria-list">
                        <li
                          v-for="cat in getCategoriasEnModalidadPorSexo(modalidad, 'MIX')"
                          :key="cat.id"
                        >
                          {{ getCategoriaNombre(cat) }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </template>
                <p v-else class="modalidad-sin-categorias">Sin categorías definidas</p>
              </div>
            </div>
          </template>
          <p v-else class="modalidades-empty">No hay modalidades cargadas para este evento.</p>
        </section>

        <!-- Galería (banner + imágenes) -->
        <section v-if="evento.banner_full_path || (evento.imagenes && evento.imagenes.length)" class="section section-galeria">
          <h2 class="section-title">
            <v-icon color="orange">photo_library</v-icon>
            Imágenes
          </h2>
          <div class="galeria-wrap">
            <v-img v-if="evento.banner_full_path" :src="evento.banner_full_path" class="galeria-img" contain></v-img>
            <v-img
              v-for="imagen in evento.imagenes"
              :key="imagen.id"
              :src="imagen.ruta_absoluta"
              class="galeria-img mt-2"
              contain
            ></v-img>
          </div>
        </section>

        <!-- CTA al fondo de la página: el usuario debe recorrer toda la info para llegar -->
        <section class="section section-cta">
          <v-btn
            v-if="!esEventoPrivado && evento.inscripcion_abierta"
            class="cta-button"
            color="orange"
            dark
            large
            block
            :to="{ name: 'inscripcion', params: { id: evento.id } }"
          >
            <v-icon left>how_to_reg</v-icon>
            Quiero inscribirme
          </v-btn>
          <v-btn
            v-else-if="esEventoPrivado && evento.inscripcion_abierta"
            class="cta-button"
            color="orange"
            dark
            large
            block
            :to="{ name: 'inscripcionPrivado', params: { id: evento.id, code: $route.params.codigo_acceso } }"
          >
            <v-icon left>how_to_reg</v-icon>
            Quiero inscribirme
          </v-btn>
          <v-btn
            v-else
            class="cta-button"
            disabled
            outlined
            color="grey"
            large
            block
          >
            <v-icon left>block</v-icon>
            Inscripción cerrada
          </v-btn>
        </section>
      </main>
      <!-- Botón volver: visible en desktop -->
      <v-btn
        class="back-button hidden-xs-only"
        dark
        fab
        fixed
        bottom
        left
        color="grey darken-2"
        @click="back"
      >
        <v-icon>arrow_back</v-icon>
      </v-btn>
      <!-- En mobile: botón volver dentro del flujo o en app bar según tengas; si no hay app bar, podemos poner un link arriba del hero -->
      <v-btn
        class="back-button-mobile"
        icon
        @click="back"
      >
        <v-icon>arrow_back</v-icon>
      </v-btn>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  data: () => ({
    loading: true,
  }),

  computed: {
    ...mapState({
      evento: state => state.eventos.eventoActual,
    }),
    esEventoPrivado() {
      return this.$route.params.codigo_acceso !== undefined;
    },
  },

  created() {
    window.scrollTo(0, 0);
    const eventoId = this.$route.params.id;
    const codigoAcceso = this.$route.params.codigo_acceso;

    this.loading = true;

    const promise = eventoId && codigoAcceso
      ? this.$store.dispatch('eventos/getFrontEventoByIdAndCodigoAcceso', { eventoId, codigoAcceso })
      : this.$store.dispatch('eventos/getFrontEventoById', eventoId);

    promise.finally(() => {
      this.loading = false;
    });
  },

  methods: {
    back() {
      this.$router.go(-1);
    },
    getCategoriaNombre(cat) {
      const edadRango = cat.sexo + cat.edad_inicial + '-' + cat.edad_final;
      return cat.nombre ? cat.nombre + ' (' + edadRango + ')' : edadRango;
    },
    getCategoriasEnModalidadPorSexo(modalidad, sexo) {
      return (modalidad.categorias || []).filter(c => c.sexo === sexo && c.visible);
    },
    hayCategoriasVisibles(modalidad) {
      return (modalidad.categorias || []).filter(c => c.visible).length > 0;
    },
  },
};
</script>

<style scoped>
.informacion-evento {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 48px;
}

.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
}

.loading-text {
  color: #757575;
  font-size: 0.95rem;
}

/* Hero: mobile first */
.evento-hero {
  position: relative;
  margin-bottom: 0;
}

.hero-image-wrap {
  width: 100%;
  height: 220px;
  overflow: hidden;
  background: #e0e0e0;
  position: relative;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px 16px 16px;
  background: linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%);
  color: white;
}

.hero-title {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 4px 0;
  color: #fff;
}

.hero-subtitle {
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.95;
  display: flex;
  align-items: center;
  gap: 4px;
}

.hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 16px 14px;
  background: #fff;
}

.hero-chip {
  font-size: 0.75rem;
}

/* Main content */
.evento-main {
  padding: 20px 16px 24px;
}

.section {
  background: #fff;
  border-radius: 12px;
  padding: 18px 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #212121;
  margin: 0 0 14px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title .v-icon {
  font-size: 22px;
}

/* Datos grid */
.datos-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dato-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.dato-card:last-child {
  border-bottom: none;
}

.dato-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.dato-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.dato-value {
  font-size: 0.95rem;
  color: #212121;
}

/* Descripción */
.descripcion-content {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #424242;
}

.descripcion-content >>> p {
  margin: 0 0 10px 0;
}

.descripcion-content >>> p:last-child {
  margin-bottom: 0;
}

/* Modalidades: bloques por modalidad */
.section-modalidades .section-title {
  margin-bottom: 16px;
}

.modalidades-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modalidad-block {
  background: #fafafa;
  border-radius: 10px;
  padding: 14px 16px;
  border-left: 4px solid #ff9800;
}

.modalidad-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.modalidad-icon {
  flex-shrink: 0;
}

.modalidad-nombre {
  font-size: 1rem;
  font-weight: 700;
  color: #212121;
  margin: 0;
}

.modalidad-sin-categorias {
  font-size: 0.875rem;
  color: #757575;
  margin: 0;
}

.modalidades-empty {
  font-size: 0.95rem;
  color: #757575;
  margin: 0;
}

.categorias-wrap {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.categoria-col {
  flex: 1;
  min-width: 0;
}

.categoria-col-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  padding-bottom: 2px;
  border-bottom: 1px solid #e0e0e0;
}

.categoria-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.9rem;
  color: #424242;
  line-height: 1.5;
}

.categoria-list li {
  padding: 2px 0;
  padding-left: 12px;
  position: relative;
}

.categoria-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #ff9800;
  font-weight: bold;
}

/* Galería */
.galeria-wrap {
  display: flex;
  flex-direction: column;
}

.galeria-img {
  border-radius: 8px;
  overflow: hidden;
  max-height: 280px;
}

/* CTA al final del contenido (no fijo) */
.section-cta {
  margin-top: 8px;
}

.section-cta .cta-button {
  text-transform: none;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.3px;
  border-radius: 12px;
  min-height: 52px;
}

/* Botón volver */
.back-button {
  z-index: 20;
  margin-bottom: 16px;
  margin-left: 16px;
}

.back-button-mobile {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  background: rgba(0,0,0,0.4);
  color: white;
}

.back-button-mobile .v-icon {
  color: white;
}

/* Tablet y desktop */
@media (min-width: 600px) {
  .hero-image-wrap {
    height: 320px;
  }

  .hero-overlay {
    padding: 28px 24px 20px;
  }

  .hero-title {
    font-size: 1.75rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .evento-main {
    max-width: 720px;
    margin: 0 auto;
    padding: 28px 24px 32px;
  }

  .section {
    padding: 24px 24px;
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 1.25rem;
    margin-bottom: 18px;
  }

  .datos-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 20px;
  }

  .dato-card {
    padding: 12px 0;
    border-bottom: none;
    border-radius: 8px;
    background: #fafafa;
    padding: 12px 14px;
  }

  .section-cta .cta-button {
    max-width: 360px;
    margin: 0 auto;
    display: block;
  }

  .informacion-evento {
    padding-bottom: 40px;
  }

  .back-button-mobile {
    display: none;
  }
}

@media (min-width: 960px) {
  .evento-main {
    max-width: 900px;
  }

  .section-datos .datos-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .categorias-wrap {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 20px 24px;
  }

  .categoria-col {
    min-width: 140px;
  }
}
</style>
